// ProductDetailComponent.jsx
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    AppBar,
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    ButtonBase,
    Chip,
    Divider,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Snackbar,
    Toolbar,
    Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VerifiedIcon from "@mui/icons-material/Verified";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {Link, useNavigate} from "react-router-dom";
import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";
import {BASE_URL_API, PRODUCT_BASE_URL_API} from "../Const";
import {ProductComponent} from "./ProductComponent";
import {CardShopRepository} from "../Repository/CardShopRepository";
import {BasketComponent} from "./BasketComponent";
import JsBarcode from "jsbarcode";
import {QRCodeSVG} from "qrcode.react";
import {Helmet} from "react-helmet-async";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Counter from "yet-another-react-lightbox/plugins/counter";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

/* ---------- helpers ---------- */
const formatPrice = (val) => Number(val || 0).toLocaleString("fa-IR");
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const pill = (bg, color) => ({
    px: 1,
    py: 0.25,
    fontSize: 12,
    height: 22,
    lineHeight: 1.1,
    borderRadius: 1,
    bgcolor: bg,
    color,
    "& .MuiChip-icon": {fontSize: 16},
});

/* =========================
   Variant helpers (based on your payload)
   ========================= */

// normalize a variant into a map: { [attribute_id]: attribute_value_id }
function variantAttrMap(variant) {
    const out = {};
    const arr = Array.isArray(variant?.attribute_values) ? variant.attribute_values : [];
    for (const av of arr) {
        if (av?.attribute_id != null && av?.id != null) {
            out[String(av.attribute_id)] = Number(av.id);
        }
    }
    return out;
}

// check if a variant matches current selected map { attrId: valueId }
function isVariantMatch(variant, selectedMap) {
    const vmap = variantAttrMap(variant);
    for (const [attrId, valId] of Object.entries(selectedMap)) {
        if (valId == null) continue;
        if (Number(vmap[String(attrId)]) !== Number(valId)) return false;
    }
    return true;
}

// find first matching variant for selectedMap
function findMatchingVariant(variants, selectedMap) {
    const list = Array.isArray(variants) ? variants : [];
    const matches = list.filter((v) => isVariantMatch(v, selectedMap));
    return matches.length ? matches[0] : null;
}

// UX: If user clicks a value that breaks combination, auto-switch to a valid variant that contains clicked value.
function pickBestVariantForClick(variants, selectedMap, attrId, candidateValueId) {
    const list = Array.isArray(variants) ? variants : [];
    const aid = String(attrId);
    const vid = Number(candidateValueId);

    const others = {...selectedMap};
    delete others[aid];

    const containsClicked = list.filter((v) => {
        const vmap = variantAttrMap(v);
        return Number(vmap[aid]) === vid;
    });
    if (!containsClicked.length) return null;

    const matchesOthers = containsClicked.filter((v) => isVariantMatch(v, {...others, [aid]: vid}));
    if (matchesOthers.length) return matchesOthers[0];

    return containsClicked[0];
}

// IMPORTANT for your CardShopRepository:
// It stores as item.product.id, so variant must have unique product.id
function buildCartProductId(productId, variantId) {
    if (variantId == null) return productId; // keep original for non-variant
    return `${productId}::${variantId}`; // unique for variant
}

/* ---------- inline Market card (clickable) ---------- */
function MarketLinkCardInline({markets, tableId}) {
    const id = markets?.id ?? 4889;
    const to = `/market?id=${id}&table_id=${tableId}`;
    const logo = markets?.logo ? `${PRODUCT_BASE_URL_API}${markets.logo}` : "https://hamekara.com/hamekara-logo.png";

    const openMap = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const lat = markets?.lat;
        const lng = markets?.long;

        if (lat != null && lng != null && String(lat).length && String(lng).length) {
            window.open(`https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}`, "_blank", "noopener,noreferrer");
            return;
        }

        const q = [markets?.name, markets?.address].filter(Boolean).join(" - ");
        if (!q) return;
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, "_blank", "noopener,noreferrer");
    };

    return (
        <ButtonBase component={Link} to={to} sx={{width: "100%", textAlign: "inherit", borderRadius: 2}}>
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    width: "100%",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.08)",
                    bgcolor: "#fff",
                    transition: "transform 160ms ease, box-shadow 160ms ease",
                    "&:hover": {transform: "translateY(-2px)", boxShadow: 4},
                    "&:active": {transform: "translateY(0)", boxShadow: 2},
                }}
            >
                <Box sx={{p: 2, pt: 2, direction: "rtl", display: "flex", alignItems: "center", gap: 1.5}}>
                    <Avatar
                        alt={markets?.name}
                        src={logo}
                        sx={{
                            width: 48,
                            height: 48,
                            border: "2px solid #fff",
                            mt: -4,
                            boxShadow: 1,
                            bgcolor: "#fff",
                        }}
                    />
                    <Box sx={{minWidth: 0, flex: 1}}>
                        <Typography variant="subtitle1" noWrap title={markets?.name} sx={{fontWeight: 800}}>
                            {markets?.name}
                        </Typography>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, flexWrap: "wrap"}}>
                            <Chip
                                icon={<VerifiedIcon/>}
                                label="فروشنده تأیید‌شده"
                                size="small"
                                sx={{
                                    px: 0.75,
                                    height: 24,
                                    borderRadius: 1,
                                    bgcolor: "rgba(0,200,83,.1)",
                                    color: "success.main",
                                    "& .MuiChip-icon": {fontSize: 16},
                                }}
                            />
                            <Chip
                                icon={<StorefrontOutlinedIcon/>}
                                label="مشاهده فروشگاه"
                                size="small"
                                sx={{
                                    px: 0.75,
                                    height: 24,
                                    borderRadius: 1,
                                    bgcolor: "grey.100",
                                    color: "text.secondary",
                                    "& .MuiChip-icon": {fontSize: 16},
                                }}
                            />
                        </Box>
                    </Box>
                    <ChevronLeftIcon sx={{color: "text.disabled"}}/>
                </Box>

                <Divider sx={{mx: 2}}/>

                {markets?.address ? (
                    <Box
                        onClick={openMap}
                        role="button"
                        tabIndex={0}
                        sx={{
                            p: 2,
                            pt: 1.25,
                            direction: "rtl",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            "&:hover": {bgcolor: "rgba(0,0,0,0.03)"},
                        }}
                    >
                        <RoomOutlinedIcon fontSize="small"/>
                        <Typography variant="body2" sx={{color: "text.secondary"}} noWrap title={markets.address}>
                            {markets.address}
                        </Typography>
                        <Typography variant="caption" sx={{color: "text.disabled", mr: "auto"}}>
                            مشاهده روی نقشه
                        </Typography>
                    </Box>
                ) : null}

                {(tableId == null || tableId === "") && (
                    <Box sx={{px: 2, pb: 2, pt: 1, direction: "rtl", display: "flex", gap: 1, flexWrap: "wrap"}}>
                        {markets?.whatsapp && markets.whatsapp !== "none" && (
                            <Button
                                variant="outlined"
                                startIcon={<WhatsAppIcon/>}
                                size="small"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(`https://wa.me/${markets.whatsapp}`, "_blank", "noopener,noreferrer");
                                }}
                                sx={{
                                    gap: 1,
                                    borderColor: "#25D366",
                                    borderRadius: "12px",
                                    color: "#25D366",
                                    fontWeight: 600,
                                    "& .MuiButton-startIcon": {color: "#25D366"},
                                    "&:hover": {
                                        borderColor: "#1DA851",
                                        backgroundColor: "rgba(37,211,102,0.08)",
                                        color: "#1DA851",
                                        "& .MuiButton-startIcon": {color: "#1DA851"},
                                    },
                                }}
                            >
                                واتساپ
                            </Button>
                        )}

                        {(markets?.tell || markets?.name) && (
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon/>}
                                size="small"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(`${markets?.name ?? ""} - ${markets?.tell ?? ""} - ${markets?.address ?? ""}`);
                                }}
                                sx={{
                                    gap: 1,
                                    borderColor: "#008ff8",
                                    borderRadius: "12px",
                                    color: "#008ff8",
                                    fontWeight: 600,
                                    "& .MuiButton-startIcon": {color: "#008ff8"},
                                    "&:hover": {
                                        borderColor: "#0b74c9",
                                        backgroundColor: "rgba(0,143,248,0.08)",
                                        color: "#0b74c9",
                                        "& .MuiButton-startIcon": {color: "#0b74c9"},
                                    },
                                }}
                            >
                                کپی اطلاعات
                            </Button>
                        )}

                        <Button
                            component={Link}
                            to={to}
                            variant="contained"
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                ml: "auto",
                                borderRadius: "12px",
                                fontWeight: 700,
                                px: 1.75,
                                gap: 1,
                                backgroundColor: "#FF1744",
                                "&:hover": {backgroundColor: "#e0143d"},
                            }}
                        >
                            ورود به فروشگاه
                        </Button>
                    </Box>
                )}

            </Paper>
        </ButtonBase>
    );
}

function BarcodeTag({code, price = null, title = "", date, rtl = true}) {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!code || !svgRef.current) return;
        try {
            JsBarcode(svgRef.current, String(code), {
                format: "CODE128",
                displayValue: true,
                font: "IRANSans, Arial, sans-serif",
                fontSize: 12,
                textMargin: 4,
                height: 48,
                margin: 0,
                lineColor: "#111",
                background: "#fff",
            });
        } catch (e) {
            console.error("Barcode render error", e);
        }
    }, [code]);

    const dateText =
        date ||
        new Date().toLocaleDateString("fa-IR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });

    return (
        <Box sx={{px: 2, mt: 2}}>
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    border: "1px solid rgba(0,0,0,0.12)",
                }}
            >
                <Box
                    sx={{
                        height: 6,
                        background: "repeating-linear-gradient(90deg, #000, #000 6px, transparent 6px, transparent 12px)",
                        opacity: 0.25,
                    }}
                />

                <Box
                    sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        direction: rtl ? "rtl" : "ltr",
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {display: "none"},
                    }}
                >
                    <Box sx={{minWidth: 0, flex: "1 1 auto"}}>
                        <Typography variant="caption" sx={{color: "text.secondary", display: "block", mb: 0.25}}>
                            بارکد
                        </Typography>

                        <Typography variant="subtitle2" sx={{fontWeight: 800, letterSpacing: 0.2}} noWrap
                                    title={String(code)}>
                            {code}
                        </Typography>

                        {title ? (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    mt: 0.25,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                                title={title}
                            >
                                {title}
                            </Typography>
                        ) : null}

                        {price != null ? (
                            <Chip
                                size="small"
                                label={`${Number(price).toLocaleString("fa-IR")} تومان`}
                                sx={{
                                    mt: 0.75,
                                    px: 1,
                                    height: 24,
                                    fontWeight: 700,
                                    color: "#0B7C3E",
                                    bgcolor: "rgba(16,185,129,.12)",
                                    borderRadius: 1.5,
                                }}
                            />
                        ) : null}

                        <Typography variant="caption" sx={{color: "text.disabled", display: "block", mt: 0.5}}>
                            {dateText}
                        </Typography>
                    </Box>

                    <Box sx={{flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap"}}>
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                bgcolor: "#fff",
                                border: "1px dashed rgba(0,0,0,0.2)",
                                borderRadius: 1,
                                p: 1,
                                maxWidth: 260,
                            }}
                        >
                            <svg ref={svgRef}/>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        px: 1.5,
                        pb: 1.5,
                        "&::before": {
                            content: '""',
                            display: "block",
                            width: "100%",
                            borderBottom: "1px dashed rgba(0,0,0,0.25)",
                        },
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        boxShadow: "0 0 0 2px rgba(0,0,0,0.25) inset",
                    }}
                />
            </Paper>
        </Box>
    );
}

/* =========================
   Variant Picker UI
   - no delete
   - no disabling values (auto-switch)
   ========================= */
function VariantPicker({categoryAttributes, variants, selectedMap, setSelectedMap, activeVariant}) {
    const optionsByAttr = useMemo(() => {
        const out = {};
        const list = Array.isArray(variants) ? variants : [];
        for (const v of list) {
            const avs = Array.isArray(v?.attribute_values) ? v.attribute_values : [];
            for (const av of avs) {
                if (av?.attribute_id == null || av?.id == null) continue;
                const k = String(av.attribute_id);
                if (!out[k]) out[k] = new Map();
                out[k].set(String(av.id), av);
            }
        }
        const final = {};
        for (const [k, mp] of Object.entries(out)) final[k] = Array.from(mp.values());
        return final;
    }, [variants]);

    const cats = Array.isArray(categoryAttributes) ? categoryAttributes : [];
    if (!cats.length) return null;

    const visibleAttrs = cats.filter((a) => optionsByAttr[String(a.id)]?.length);
    if (!visibleAttrs.length) return null;

    return (
        <Box sx={{px: 2, mt: 2}}>
            <Typography variant="subtitle1" sx={{color: "text.primary", fontWeight: 800, fontSize: "1.05rem", mb: 1}}>
                انتخاب تنوع
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.08)",
                    bgcolor: "#fff",
                }}
            >
                {visibleAttrs.map((attr) => {
                    const attrId = attr.id;
                    const options = optionsByAttr[String(attrId)] || [];
                    const selectedValId = selectedMap[String(attrId)] ?? null;

                    return (
                        <Box key={attrId} sx={{mb: 1.25}}>
                            <Box
                                sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75}}>
                                <Typography variant="body2" sx={{fontWeight: 800, color: "text.secondary"}}>
                                    {attr.title}
                                </Typography>
                            </Box>

                            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                                {options.map((opt) => {
                                    const optId = opt.id;
                                    const isSelected = Number(selectedValId) === Number(optId);

                                    return (
                                        <Chip
                                            key={optId}
                                            clickable
                                            onClick={() => {
                                                const nextMap = {...selectedMap, [String(attrId)]: Number(optId)};

                                                const direct = findMatchingVariant(variants, nextMap);
                                                if (direct) {
                                                    setSelectedMap(nextMap);
                                                    return;
                                                }

                                                const best = pickBestVariantForClick(variants, selectedMap, attrId, optId);
                                                if (!best) {
                                                    setSelectedMap(nextMap);
                                                    return;
                                                }

                                                setSelectedMap(variantAttrMap(best));
                                            }}
                                            label={opt?.value ?? ""}
                                            variant={isSelected ? "filled" : "outlined"}
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 800,
                                                ...(isSelected
                                                    ? {
                                                        bgcolor: "rgba(255, 23, 68, .12)",
                                                        color: "#d50000",
                                                        borderColor: "rgba(213,0,0,.35)",
                                                    }
                                                    : {}),
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            <Divider sx={{mt: 1.25}}/>
                        </Box>
                    );
                })}

                <Box sx={{mt: 1}}>
                    {activeVariant ? (
                        <Box sx={{display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center"}}>
                            <Chip
                                size="small"
                                icon={<VerifiedIcon/>}
                                label={`تنوع انتخاب شده: ${activeVariant.title ?? `#${activeVariant.id}`}`}
                                sx={{
                                    px: 0.75,
                                    height: 28,
                                    borderRadius: 2,
                                    bgcolor: "rgba(0,200,83,.12)",
                                    color: "success.main",
                                    fontWeight: 900,
                                }}
                            />
                            {activeVariant?.barcode ? (
                                <Chip size="small" label={`بارکد: ${activeVariant.barcode}`}
                                      sx={{height: 28, borderRadius: 2, fontWeight: 800}}/>
                            ) : null}
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{color: "text.secondary", fontWeight: 700}}>
                            یک ترکیب معتبر از مقادیر انتخاب کنید.
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

export default function ProductDetailComponent({id, tableId}) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [copied, setCopied] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");
    const [updateBasket, setUpdateBasket] = useState(0);

    // Variant state
    const [selectedMap, setSelectedMap] = useState({});
    const [activeVariant, setActiveVariant] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const [activeImage, setActiveImage] = useState(0);
    const carouselRef = useRef();
    /* ---------- fetch ---------- */
    useEffect(() => {
        if (!id) return;
        let alive = true;

        setLoading(true);
        setData(null);
        setSelectedMap({});
        setActiveVariant(null);

        fetch(`${BASE_URL_API}product/${id}`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("pwa_token")}`,
                "Content-Type": "application/json",
            },
        })
            .then((r) => r.json())
            .then((json) => {
                if (!alive) return;
                if (json?.code === 200) setData(json.data);
                setLoading(false);
            })
            .catch(() => alive && setLoading(false));

        return () => {
            alive = false;
        };
    }, [id]);

    /* ---------- ✅ default select FIRST variant if exists ---------- */
    useEffect(() => {
        if (!data) return;

        const variants = Array.isArray(data?.variants) ? data.variants : [];
        if (!variants.length) {
            setSelectedMap({});
            setActiveVariant(null);
            return;
        }

        const first = variants[0];
        setSelectedMap(variantAttrMap(first));
        setActiveVariant(first);
    }, [data]);

    /* ---------- active variant update when selection changes ---------- */
    useEffect(() => {
        if (!data) return;
        const variants = Array.isArray(data?.variants) ? data.variants : [];
        if (!variants.length) {
            setActiveVariant(null);
            return;
        }
        setActiveVariant(findMatchingVariant(variants, selectedMap));
    }, [data, selectedMap]);

    /* ---------- share / copy ---------- */
    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({title: data?.title || "محصول", url});
                return;
            }
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        }
    };

    /* ---------- early states ---------- */
    if (loading) {
        return (
            <Box sx={{p: 2}}>
                <Skeleton variant="rectangular" height={260} sx={{borderRadius: 2}}/>
                <Skeleton variant="text" height={32} sx={{mt: 2, width: "60%"}}/>
                <Skeleton variant="text" width="40%"/>
                <Skeleton variant="rectangular" height={120} sx={{mt: 2, borderRadius: 2}}/>
            </Box>
        );
    }

    if (!data) return <Box sx={{p: 3}}>محصول پیدا نشد.</Box>;

    /* ---------- safe derived data ---------- */
    const baseProductId = data?.id ?? id;
    const baseStep = data?.step ?? 0;
    const baseTitle = data?.title ?? "";
    const basePrice = Number(data?.price ?? 0);
    const baseDelPrice = Number(data?.del_price ?? 0);
    const baseBarcode = data?.barcode ?? "";
    const des = data?.des ?? "";
    const basePictures = data?.pictures ?? [];
    const category = data?.category ?? null;
    const markets = data?.markets ?? null;

    const hasVariants = Array.isArray(data?.variants) && data.variants.length > 0;

    const displayTitle = activeVariant?.title ? `${baseTitle} - ${activeVariant.title}` : baseTitle;

    const displayPictures =
        Array.isArray(activeVariant?.pictures) && activeVariant.pictures.length ? activeVariant.pictures : basePictures;

    const lightboxSlides = displayPictures.map((pic) => ({
        src: `${PRODUCT_BASE_URL_API}${pic.url}`,
    }));

    const displayBarcode = activeVariant?.barcode || baseBarcode;

    const displayStep = activeVariant?.step != null ? activeVariant.step : baseStep;

    const isVariantMode = Boolean(activeVariant);

    // 1. Get the seller discount percentage first
    const variantSellerDiscount = isVariantMode ? Number(activeVariant?.seller_discount ?? 0) : 0;

    const displayPrice = isVariantMode ? Number(activeVariant?.customer_price ?? 0) : basePrice;

    // 2. Calculate the actual discount amount for variants based on the percentage
    const displayDelPrice = isVariantMode
        ? Math.floor(displayPrice * (variantSellerDiscount / 100))
        : baseDelPrice;

    const hasDiscount = displayDelPrice > 0;
    const finalPrice = Math.floor(hasDiscount ? displayPrice - displayDelPrice : displayPrice);

    const discountPct =
        hasDiscount && displayPrice
            ? clamp(Math.round((displayDelPrice / displayPrice) * 100), 1, 95)
            : 0;

    const inStock = Number(displayStep) > 0;

    // ✅ disable add-to-cart:
    // - must be inStock
    // - finalPrice > 0
    // - if hasVariants => must have activeVariant
    const isPayable = inStock && Number(finalPrice) > 0 && (!hasVariants || Boolean(activeVariant));

    // ---------- SEO / Canonical ----------
    const canonicalUrl = typeof window !== "undefined" ? window.location.href.split("#")[0] : "";
    const seoTitle = baseTitle ? `${baseTitle} | همه‌کارا` : "همه‌کارا | فروشگاه آنلاین و آفلاین";
    const seoDescription =
        des && des.length > 0
            ? des.slice(0, 155)
            : "خرید آنلاین محصول از فروشگاه‌های متصل به سامانه همه‌کارا با امکان ثبت سفارش و مشاهده جزئیات کامل محصول.";

    const marketId = data?.market_id;
    const categoryId = category?.id;

    const categoryListUrl =
        marketId && categoryId ? `https://hamekara.com/product-list?market_id=${marketId}&category_id=${categoryId}` : null;

    const showToast = (message, type = "success") => {
        setSnackbarMessage(message);
        setSnackbarType(type);
        setOpenSnackbar(true);
    };

    /* ---------- add to cart (100% compatible with your CardShopRepository) ---------- */
    const onAddToCardClicked = () => {
        if (hasVariants && !activeVariant) {
            showToast("لطفاً ابتدا تنوع را انتخاب کنید.", "error");
            return;
        }
        if (!inStock) {
            showToast("موجودی کالا به اتمام رسیده!", "error");
            return;
        }
        if (finalPrice <= 0) {
            showToast("جهت خرید کالا تماس بگیرید.", "error");
            return;
        }

        const variantId = activeVariant?.id ?? null;
        const cartProductId = buildCartProductId(baseProductId, variantId);

        // IMPORTANT: repository compares item.product.id === id
        // so for variant, we set productItem.id to "productId::variantId"
        const cartItem = activeVariant
            ? {
                ...data,
                id: cartProductId, // ✅ unique key for repository
                product_id: baseProductId, // keep original product id
                variant_id: variantId,
                variant: activeVariant,

                title: displayTitle,
                step: displayStep,
                barcode: displayBarcode,
                pictures: displayPictures,

                price: displayPrice,
                del_price: displayDelPrice, // <-- Updated this to use the calculated discount amount
                final_price: finalPrice,

                selected_attribute_values: activeVariant.attribute_values || [],
            }
            : {
                ...data,
                id: baseProductId, // ✅ keep old behavior
                product_id: baseProductId,
            };

        // now repository works correctly
        const findCardShop = CardShopRepository.getItem(cartItem.id);

        if (findCardShop) {
            const totalEntity = parseInt(findCardShop.count);
            if (totalEntity < parseInt(displayStep)) {
                CardShopRepository.addCardShop(cartItem);
                setUpdateBasket(Math.random());
                showToast("کالا با موفقیت به سبد خرید اضافه شد.");
            } else {
                showToast("موجودی کالا به اتمام رسیده!", "error");
            }
        } else {
            CardShopRepository.addCardShop(cartItem);
            setUpdateBasket(Math.random());
            showToast("کالا با موفقیت به سبد خرید اضافه شد.");
        }
    };


    return (
        <>
            <Helmet>
                {canonicalUrl && <link rel="canonical" href={canonicalUrl}/>}
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription}/>
                <meta property="og:type" content="product"/>
                <meta property="og:title" content={seoTitle}/>
                <meta property="og:description" content={seoDescription}/>
                {displayPictures?.[0]?.url &&
                    <meta property="og:image" content={`${PRODUCT_BASE_URL_API}${displayPictures[0].url}`}/>}
            </Helmet>

            {/* Top AppBar */}
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: "rgba(255,255,255,0.55)",
                    backdropFilter: "blur(10px)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
            >
                <Toolbar sx={{gap: 0.5}}>
                    <IconButton onClick={() => window.close()} size="large" edge="start">
                        <ArrowForwardIosIcon sx={{color: "text.primary"}}/>
                    </IconButton>

                    <Typography noWrap sx={{flexGrow: 1, fontWeight: 700, color: "text.primary", pr: 1}}
                                title={baseTitle}>
                        {baseTitle}
                    </Typography>

                    {(tableId == null || tableId === "") && (
                        <IconButton onClick={handleShare} size="large">
                            <ShareIcon sx={{color: "text.primary"}}/>
                        </IconButton>
                    )}


                    <IconButton size="large">
                        <BasketComponent updateBasket={updateBasket} table_id={tableId}/>
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Content */}
            <Box sx={{pt: 9.5, pb: 12, direction: "rtl"}}>
                {/* Hero images */}
                <Box sx={{borderRadius: 2, overflow: "hidden", mx: 2, position: "relative"}}>
                    {displayPictures?.length ? (
                        <AliceCarousel
                            ref={carouselRef}
                            key={activeVariant?.id || "base"}
                            disableButtonsControls
                            disableDotsControls
                            infinite={displayPictures.length > 1}
                            autoPlay={displayPictures.length > 1}
                            autoPlayInterval={3500}
                            animationDuration={700}
                            mouseTracking
                            onSlideChanged={({ item }) => setActiveImage(item)}
                        >
                            {displayPictures.map((pic, idx) => (
                                <Box
                                    sx={{
                                        height: { xs: 260, sm: 320 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#fff",
                                    }}
                                >

                                        <img
                                            src={`${PRODUCT_BASE_URL_API}${pic.url}`}
                                            alt=""
                                            onClick={() => {
                                                setLightboxIndex(idx);
                                                setLightboxOpen(true);
                                            }}
                                            style={{
                                                display: "block",
                                                maxWidth: "100%",
                                                maxHeight: "320px",
                                                width: "auto",
                                                height: "auto",
                                                objectFit: "contain",
                                                cursor: "zoom-in",
                                            }}
                                        />
                                </Box>
                            ))}
                        </AliceCarousel>

                    ) : (
                        <Paper
                            sx={{
                                height: {xs: 260, sm: 320},
                                backgroundImage: `url(/hamekara-logo.png)`,
                                backgroundSize: "contain",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                            }}
                        />
                    )}

                    {displayPictures.length > 1 && (
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 12,
                                right: 12,
                                bgcolor: "rgba(0,0,0,.55)",
                                color: "#fff",
                                px: 1.2,
                                py: 0.4,
                                borderRadius: 10,
                                fontWeight: 700,
                                fontSize: 13,
                                backdropFilter: "blur(8px)",
                                zIndex: 10,
                            }}
                        >
                            {activeImage + 1} / {displayPictures.length}
                        </Box>
                    )}

                    {displayPictures.length > 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                overflowX: "auto",
                                mt: 1.5,
                                px: 1,
                                pb: .5,
                                "&::-webkit-scrollbar": {
                                    display: "none"
                                }
                            }}
                        >
                            {displayPictures.map((pic, index) => (
                                <Box
                                    key={index}
                                    onClick={() => {
                                        carouselRef.current?.slideTo(index);
                                        setActiveImage(index);
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        cursor: "pointer",
                                        transition: ".25s",
                                        border:
                                            activeImage === index
                                                ? "2px solid #ff1744"
                                                : "2px solid transparent",
                                        transform:
                                            activeImage === index
                                                ? "scale(1.05)"
                                                : "scale(1)",
                                        opacity:
                                            activeImage === index
                                                ? 1
                                                : .65,
                                        boxShadow:
                                            activeImage === index
                                                ? 3
                                                : 0,

                                        "&:hover": {
                                            opacity: 1
                                        }
                                    }}
                                >
                                    <img
                                        src={`${PRODUCT_BASE_URL_API}${pic.url}`}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover"
                                        }}
                                    />


                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box
                        sx={{
                            position: "absolute",
                            inset: "auto 0 0 0",
                            height: 80,
                            background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.45) 100%)",
                            pointerEvents: "none",
                        }}
                    />

                    <Box sx={{position: "absolute", top: 10, left: 10, display: "flex", gap: 1, flexWrap: "wrap"}}>
                        {hasDiscount && <Chip icon={<LocalOfferOutlinedIcon/>} label={`${discountPct}% تخفیف`}
                                              sx={pill("rgba(255, 23, 68, .9)", "#fff")}/>}

                        {isVariantMode && variantSellerDiscount > 0 ? (
                            <Chip icon={<LocalOfferOutlinedIcon/>} label={`تخفیف فروشنده: ${variantSellerDiscount}%`}
                                  sx={pill("rgba(255, 152, 0, .9)", "#fff")}/>
                        ) : null}

                        {inStock ? (
                            <Chip icon={<VerifiedIcon/>} label="موجود" sx={pill("rgba(0, 200, 83, .95)", "#fff")}/>
                        ) : (
                            <Chip label="ناموجود" sx={pill("rgba(244, 67, 54, .95)", "#fff")}/>
                        )}
                    </Box>
                </Box>

                {/* Title + crumbs + QR */}
                <Box sx={{px: 2, mt: 2}}>
                    <Box
                        sx={{
                            direction: "rtl",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": {display: "none"},
                        }}
                    >
                        <Box sx={{minWidth: 0, flex: "1 1 auto"}}>
                            <Typography variant="h6"
                                        sx={{fontWeight: 800, mb: 0.5, whiteSpace: "normal", wordBreak: "break-word"}}>
                                {displayTitle}
                            </Typography>

                            <Breadcrumbs
                                separator="›"
                                sx={{
                                    "& .MuiBreadcrumbs-ol": {justifyContent: "flex-start"},
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {category?.parent?.title && (
                                    <Chip
                                        size="small"
                                        label={category.parent.title}
                                        sx={{
                                            bgcolor: "grey.100",
                                            "&:hover": {bgcolor: "grey.200"},
                                            height: 24,
                                            fontSize: 12,
                                            maxWidth: 160,
                                            "& .MuiChip-label": {overflow: "hidden", textOverflow: "ellipsis"},
                                        }}
                                    />
                                )}

                                {category?.title && categoryListUrl && (
                                    <a href={categoryListUrl} style={{textDecoration: "none"}}>
                                        <Chip
                                            size="small"
                                            label={category.title}
                                            sx={{
                                                bgcolor: "grey.100",
                                                "&:hover": {bgcolor: "grey.200"},
                                                height: 24,
                                                fontSize: 12,
                                                maxWidth: 160,
                                                "& .MuiChip-label": {overflow: "hidden", textOverflow: "ellipsis"},
                                            }}
                                        />
                                    </a>
                                )}
                            </Breadcrumbs>
                        </Box>

                        <Box
                            sx={{
                                flex: "0 0 auto",
                                p: 0.75,
                                borderRadius: 1.5,
                                border: "1px dashed rgba(0,0,0,0.25)",
                                bgcolor: "grey.200",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                            }}
                            aria-label="کد QR برای اسکن"
                            title="کد QR برای اسکن"
                        >
                            <QRCodeSVG value={String(window.location.href)} size={60} bgColor="#ffffff"
                                       fgColor="#000000" level="Q" includeMargin={false}/>
                            <Typography variant="caption" sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "text.secondary",
                                textTransform: "uppercase",
                                letterSpacing: 2
                            }}>
                                !Scan me
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Variant picker only if variants exist */}
                {hasVariants ? (
                    <VariantPicker
                        categoryAttributes={category?.attributes}
                        variants={data?.variants}
                        selectedMap={selectedMap}
                        setSelectedMap={setSelectedMap}
                        activeVariant={activeVariant}
                    />
                ) : null}

                {/* Price & stock */}
                <Box sx={{p: 2}}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            p: 2,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,248,248,1) 100%)",
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                            <Inventory2Icon fontSize="small" sx={{color: inStock ? "success.main" : "error.main"}}/>
                            <Typography variant="body2"
                                        sx={{color: inStock ? "text.secondary" : "error.main", fontWeight: 600}}>
                                {inStock ? `موجودی: ${Number(displayStep).toLocaleString("en", {maximumFractionDigits: 4})}` : "تماس بگیرید"}
                            </Typography>
                        </Box>

                        {hasDiscount && (
                            <Typography variant="body2" sx={{color: "text.disabled", textDecoration: "line-through"}}>
                                {formatPrice(displayPrice)} تومان
                            </Typography>
                        )}

                        <Typography variant="h5" sx={{
                            fontWeight: 800,
                            color: isPayable ? "success.main" : "warning.main",
                            mt: 0.25
                        }}>
                            {isPayable ? `${formatPrice(finalPrice)} تومان` : "قیمت: تماس بگیرید"}
                        </Typography>
                    </Paper>
                </Box>

                {/* Market card */}
                {markets && (
                    <Box sx={{px: 2, mt: 2}}>
                        <MarketLinkCardInline markets={markets} tableId={tableId}/>
                    </Box>
                )}

                {/* Description */}
                {des && (
                    <Box sx={{px: 2, mt: 2}}>
                        <Typography variant="subtitle1"
                                    sx={{color: "text.primary", fontWeight: 800, fontSize: "1.05rem", mb: 1}}>
                            توضیحات
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: "pre-line",
                                color: "#333",
                                fontWeight: 600,
                                lineHeight: 2,
                                fontSize: "0.95rem",
                            }}
                        >
                            {des}
                        </Typography>
                    </Box>
                )}

                {/* Barcode */}
                {displayBarcode &&
                    <BarcodeTag code={displayBarcode} price={isPayable ? finalPrice : null} title={displayTitle}/>}

                {/* Similar products */}
                {data?.market_id && categoryId && (
                    <Box sx={{px: 2, mt: 3}}>
                        <Typography variant="subtitle2" sx={{color: "text.primary", fontWeight: 700, mb: 0.5}}>
                            محصولات مشابه
                        </Typography>
                        <ProductComponent market_id={data.market_id} category_id={category?.parent?.id}
                                          scroll_to_top={true} table_id={tableId}/>
                    </Box>
                )}

                <Box sx={{height: 80}}/>
            </Box>

            {/* Bottom action bar */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 1,
                    background: "rgba(255,255,255,.9)",
                    backdropFilter: "blur(10px)",
                    borderTop: "1px solid rgba(0,0,0,.06)",
                }}
            >
                <Box sx={{display: "flex", gap: 1.25}}>
                    <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        disabled={!isPayable}
                        onClick={onAddToCardClicked}
                        startIcon={<AddShoppingCartIcon/>}
                        sx={{
                            gap: 1.2,
                            borderRadius: 2,
                            fontWeight: 800,
                            py: 1.25,
                            backgroundColor: isPayable ? "#ff1744" : "grey.400",
                            color: isPayable ? "#fff" : "grey.700",
                            "&:hover": isPayable ? {backgroundColor: "#e0143d"} : {},
                        }}
                    >
                        افزودن به سبد
                    </Button>

                    {(tableId == null || tableId === "") && (
                        <Button variant="outlined" onClick={handleShare} sx={{borderRadius: 2, minWidth: 56, px: 1.5}}>
                            <ShareIcon/>
                        </Button>
                    )}

                </Box>
            </Box>

            {/* Snackbars */}
            <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)}
                      anchorOrigin={{vertical: "top", horizontal: "center"}}>
                <Alert severity="success" variant="filled">
                    لینک محصول کپی شد!
                </Alert>
            </Snackbar>

            <Snackbar open={openSnackbar} autoHideDuration={2000} onClose={() => setOpenSnackbar(false)}
                      anchorOrigin={{vertical: "top", horizontal: "center"}}>
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarType} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                index={lightboxIndex}
                slides={lightboxSlides}
                plugins={[Zoom, Thumbnails, Counter]}
            />

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    backgroundColor: "#1e1e1e",
                    color: "#f1f1f1",
                    px: 3,
                    py: 4,
                    mt: 6,
                    fontSize: 14,
                    pb: "70px",
                    textAlign: "center",
                }}
            >
                <Grid container spacing={3} justifyContent="center">
                    <Grid item xs={12} sm={4}>
                        <Typography variant="h6" sx={{fontWeight: "bold", mb: 1}}>
                            درباره همه کارا
                        </Typography>
                        <Typography variant="body2" sx={{mb: 1}}>
                            سامانه فروشگاهی <strong>همه کارا</strong> راهکاری نوین برای مدیریت فروشگاه‌های آنلاین و
                            آفلاین است.
                        </Typography>
                        <a href="https://intro.hamekara.com/" target="_blank" rel="noopener noreferrer"
                           style={{color: "#4fc3f7", textDecoration: "none", fontWeight: "bold"}}>
                            معرفی کامل همه کارا
                        </a>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Typography variant="h6" sx={{fontWeight: "bold", mb: 1}}>
                            تماس با ما
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>
                            📞 0383-228-5853
                        </Typography>
                        <Typography variant="body2" sx={{mb: 0.5}}>
                            📧 thetoptraders@gmail.com
                        </Typography>
                        <Typography variant="body2">📍 ایران - شهرکرد - ساختمان علم و فناوری پردیس - طبقه سوم - واحد
                            305</Typography>
                    </Grid>

                    <Grid item xs={12} sm={4} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <div className="trust-badge">
                            <a referrerPolicy="origin" target="_blank"
                               href="https://trustseal.enamad.ir/?id=618220&Code=W86FGXOVhlD2RNWlNLWwxE5EVujECL5N">
                                <img
                                    referrerPolicy="origin"
                                    src="https://trustseal.enamad.ir/logo.aspx?id=618220&Code=W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                    alt=""
                                    style={{cursor: "pointer"}}
                                    code="W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                />
                            </a>
                        </div>
                    </Grid>
                </Grid>

                <Divider sx={{my: 3, bgcolor: "#555"}}/>

                <Typography variant="caption" sx={{color: "#aaa"}}>
                    © {new Date().getFullYear()} Hamekara.com - کلیه حقوق محفوظ است
                </Typography>
            </Box>
        </>
    );
}
